// =============================================================================
// Investor Messages Page
// =============================================================================

import { useState, useEffect } from 'react';
import { Card, Button } from '../../components/ui';
import api from '../../services/api';
import { formatMessageDate } from '../../utils/dateFormat';

interface Attachment {
  id: string;
  name: string;
  mimeType?: string;
  fileSize?: number;
}

interface Message {
  id: string;
  subject: string;
  body: string;
  fromUser: {
    name: string;
    isAdmin: boolean;
  } | null;
  toAdmin: boolean;
  isSentByMe: boolean;
  readAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  attachments?: Attachment[];
}

type FilterType = 'inbox' | 'sent' | 'archived';

export function MessagesPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [filter, setFilter] = useState<FilterType>('inbox');

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeFiles, setComposeFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  // Archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Message | null>(null);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [filter]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      // Map 'inbox' to 'all' for the API
      const apiFilter = filter === 'inbox' ? 'all' : filter;
      const response = await api.get<{ success: boolean; data: Message[] }>(
        `/investor/messages?filter=${apiFilter}`
      );
      setMessages(response.data.data);
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = async (message: Message) => {
    setSelectedMessage(message);

    // Mark as read if not already read and it's not sent by me
    if (!message.readAt && !message.isSentByMe) {
      try {
        // Instantly decrement sidebar badge + notification bell (no API wait)
        window.dispatchEvent(new CustomEvent('badge-decrement', { detail: { messages: -1, messageId: message.id } }));
        // Optimistically update local list
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, readAt: new Date().toISOString() } : m))
        );
        // Then persist to server
        await api.put(`/investor/messages/${message.id}/read`);
      } catch (err) {
        console.error('Failed to mark message as read:', err);
      }
    }
  };

  const handleArchiveClick = (message: Message) => {
    setArchiveTarget(message);
    setShowArchiveConfirm(true);
  };

  const handleArchiveConfirm = async () => {
    if (!archiveTarget) return;

    try {
      setArchiving(true);
      await api.put(`/investor/messages/${archiveTarget.id}/archive`);
      setShowArchiveConfirm(false);
      setArchiveTarget(null);
      setSelectedMessage(null);
      loadMessages();
    } catch (err) {
      console.error('Failed to archive message:', err);
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async (messageId: string) => {
    try {
      await api.put(`/investor/messages/${messageId}/unarchive`);
      loadMessages();
      setSelectedMessage(null);
    } catch (err) {
      console.error('Failed to unarchive message:', err);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!composeSubject.trim() || !composeBody.trim()) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('subject', composeSubject);
      formData.append('body', composeBody);
      composeFiles.forEach((file) => formData.append('files', file));

      await api.post('/investor/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowCompose(false);
      setComposeSubject('');
      setComposeBody('');
      setComposeFiles([]);
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  // Use centralized PST date formatting
  const formatDate = formatMessageDate;

  const unreadCount = messages.filter((m) => !m.readAt && !m.isSentByMe).length;

  if (error && !loading) {
    return (
      <div className="text-center py-12 text-red-600">{error}</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'Communicate with admin'}
          </p>
        </div>
        <Button onClick={() => setShowCompose(true)}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New Message
        </Button>
      </div>

      {/* Filter Tabs - Inbox, Sent, Archived */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setFilter('inbox')}
          className={`pb-3 px-1 font-medium text-sm border-b-2 flex items-center gap-2 ${
            filter === 'inbox'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Inbox
          {filter === 'inbox' && unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setFilter('sent')}
          className={`pb-3 px-1 font-medium text-sm border-b-2 ${
            filter === 'sent'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Sent
        </button>
        <button
          onClick={() => setFilter('archived')}
          className={`pb-3 px-1 font-medium text-sm border-b-2 ${
            filter === 'archived'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Archived
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-900" />
        </div>
      ) : messages.length === 0 && !showCompose ? (
        <Card className="p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 mb-4">
            {filter === 'archived' ? 'No archived messages' :
             filter === 'sent' ? 'No sent messages' : 'No messages in inbox'}
          </p>
          {filter === 'inbox' && (
            <Button onClick={() => setShowCompose(true)}>Send a message to admin</Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1">
            <Card className="divide-y max-h-[600px] overflow-y-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setShowCompose(false);
                    handleSelectMessage(message);
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 ${
                    selectedMessage?.id === message.id && !showCompose ? 'bg-primary-50' : ''
                  } ${!message.readAt && !message.isSentByMe ? 'border-l-4 border-l-primary-600' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`text-sm truncate ${!message.readAt && !message.isSentByMe ? 'font-semibold' : 'font-medium'} text-gray-900`}>
                          {message.subject}
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {message.isSentByMe ? 'To: Admin' : `From: ${message.fromUser?.name || 'Admin'}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(message.createdAt)}
                      </p>
                    </div>
                    {!message.readAt && !message.isSentByMe && (
                      <span className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0 mt-1.5" />
                    )}
                    {message.isSentByMe && (
                      <span className="text-xs text-gray-400">Sent</span>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Message Detail / Compose */}
          <div className="lg:col-span-2">
            {showCompose ? (
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">New Message to Admin</h2>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter subject..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      required
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Write your message..."
                    />
                  </div>
                  {/* File Attachments */}
                  <div>
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      Attach Files
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const selected = e.target.files ? Array.from(e.target.files) : [];
                          if (selected.length > 0) {
                            setComposeFiles((prev) => [...prev, ...selected]);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {composeFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {composeFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between px-3 py-1.5 bg-gray-50 rounded text-sm">
                            <span className="truncate mr-2">{file.name} ({(file.size / 1024).toFixed(0)} KB)</span>
                            <button
                              type="button"
                              onClick={() => setComposeFiles((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-red-400 hover:text-red-600 flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setShowCompose(false);
                        setComposeSubject('');
                        setComposeBody('');
                        setComposeFiles([]);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={sending}>
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </form>
              </Card>
            ) : selectedMessage ? (
              <Card className="p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">{selectedMessage.subject}</h2>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span>
                      {selectedMessage.isSentByMe ? 'To: Admin' : `From: ${selectedMessage.fromUser?.name || 'Admin'}`}
                    </span>
                    <span>{formatDate(selectedMessage.createdAt)}</span>
                  </div>
                </div>
                <div className="prose max-w-none border-t pt-4 pb-4">
                  <p className="whitespace-pre-wrap">{selectedMessage.body}</p>
                </div>
                {/* Attachments */}
                {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                  <div className="pb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Attachments ({selectedMessage.attachments.length})</p>
                    <div className="space-y-1">
                      {selectedMessage.attachments.map((att) => (
                        <button
                          key={att.id}
                          onClick={async () => {
                            try {
                              const response = await api.get(
                                `/investor/messages/${selectedMessage.id}/attachments/${att.id}/download`,
                                { responseType: 'blob' }
                              );
                              const url = window.URL.createObjectURL(response.data);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = att.name;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              window.URL.revokeObjectURL(url);
                            } catch {
                              console.error('Failed to download attachment');
                            }
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm w-full text-left"
                        >
                          <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="truncate">{att.name}</span>
                          {att.fileSize && (
                            <span className="text-gray-400 text-xs flex-shrink-0">
                              ({att.fileSize > 1048576 ? `${(att.fileSize / 1048576).toFixed(1)} MB` : `${(att.fileSize / 1024).toFixed(0)} KB`})
                            </span>
                          )}
                          <svg className="w-4 h-4 text-primary-500 ml-auto flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Action buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <div className="flex gap-2">
                    {selectedMessage.archivedAt ? (
                      <button
                        onClick={() => handleUnarchive(selectedMessage.id)}
                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Move to Inbox
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchiveClick(selectedMessage)}
                        className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {/* Reply button - only for received messages */}
                    {!selectedMessage.isSentByMe && (
                      <button
                        onClick={() => {
                          const reSubject = selectedMessage.subject.startsWith('Re: ')
                            ? selectedMessage.subject
                            : `Re: ${selectedMessage.subject}`;
                          setComposeSubject(reSubject);
                          setComposeBody('');
                          setShowCompose(true);
                          setSelectedMessage(null);
                        }}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        Reply
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-gray-500">Select a message to read</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && archiveTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-full">
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Archive Message</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to archive this message? You can find it later in the Archived tab.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setArchiveTarget(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                disabled={archiving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {archiving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Archiving...
                  </>
                ) : (
                  'Archive'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessagesPage;
